import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { login } from '../../actions/authActions';
import axiosInstance from '../../api/axiosInstance';
import { User } from 'lucide-react';
import BackArrow from '../../assets/images/Frame_18177.png';
import FemaleGenderImg from '../../assets/images/gender.png';
import MaleGenderImg from '../../assets/images/male_gender.png';
import InactivityTimer from '../../components/InactivityTimer';

const SelectPatientProfile = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const [patients, setPatients] = useState([]);
    const [mobileNumber, setMobileNumber] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        // Get patient list from navigation state
        if (location.state?.patients && location.state?.mobileNumber) {
            setPatients(location.state.patients);
            setMobileNumber(location.state.mobileNumber);
        } else {
            // Redirect to login if no data
            navigate('/login', { replace: true });
        }
    }, [location, navigate]);

    const calculateAge = (dob) => {
        if (!dob) return 'N/A';
        const birthDate = new Date(dob);
        const today = new Date();
        let years = today.getFullYear() - birthDate.getFullYear();
        let months = today.getMonth() - birthDate.getMonth();
        
        if (months < 0) {
            years--;
            months += 12;
        }
        
        return `${years} Years ${months} Months`;
    };

    const formatDate = (dob) => {
        if (!dob) return '';
        const date = new Date(dob);
        const day = String(date.getDate()).padStart(2, '0');
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    const getGenderImage = (genderCode) => {
        if (genderCode === 'F' || genderCode === 'Female') {
            return FemaleGenderImg;
        }
        return MaleGenderImg;
    };

    const getGenderText = (genderCode) => {
        if (genderCode === 'F') return 'Female';
        if (genderCode === 'M') return 'Male';
        if (genderCode === 'O') return 'Other';
        return genderCode || 'N/A';
    };

    const handleProfileSelect = async (patientId) => {
        setLoading(true);
        setError('');
        
        try {
            const response = await axiosInstance.post('/api/auth/select-profile', {
                patientId: patientId,
                mobileNumber: mobileNumber
            });

            const kiosk_token = response.data.kiosk_token;
            dispatch(login(kiosk_token));
            navigate('/dashboard', { replace: true });
        } catch (error) {
            console.error('Error selecting profile:', error);
            setError(error.response?.data?.errors?.general || 'Failed to select profile. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const getFullName = (patient) => {
        const prefix = patient.prefix || '';
        const firstName = patient.firstName || '';
        const middleName = patient.middleName || '';
        const lastName = patient.lastName || '';
        return `${prefix} ${firstName} ${middleName} ${lastName}`.trim();
    };
  return (
    <>
        <InactivityTimer />
        <div>
        {/* Back to Home Button */}
        <div className='flex'>
            <Link to="/" className="flex w-28 mt-6 ml-14 px-4 py-3 bg-[#0FA968] text-white font-semibold rounded-lg shadow hover:bg-[#138F5E] transition duration-200" style={{ fontFamily: 'Roboto', fontSize: '20px', fontWeight: '500', lineHeight: '23.44px' }}>
                <img src={BackArrow} alt="Back" className="mr-2" style={{ height: '24px', width: '24px' }} />
                Back
            </Link>
            <span className='mt-8 ml-4' style={{ fontFamily: 'Roboto', fontSize: '28px', fontWeight: '600', lineHeight: '32.81px' }}>
                Already Registered
            </span>
        </div>

        {/* Horizontal Line */}
        <hr className="mt-6 ml-14 mr-14 border-t-1 border-gray-300" />

        <div className="flex justify-center mt-10 mb-10">
            <div className="bg-white shadow-lg rounded-lg flex flex-col w-full max-w-lg">
                <div className='p-6'>
                    <p className="font-robot text-[#0FA968]" style={{fontWeight: "700", fontSize: "24px"}}>Select Patient Profile</p>
                    <p className="font-robot text-[#42424A]" style={{fontWeight: "400", fontSize: "12px"}}>We found {patients.length} patient{patients.length > 1 ? 's' : ''} registered with this mobile number</p>
                </div>

                <hr className="border-t-1 border-gray-300" />
                <div className='p-6'>
                    {error && (
                        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
                            {error}
                        </div>
                    )}

                    {patients.length === 0 ? (
                        <p className="text-center text-gray-500">No patients found</p>
                    ) : (
                        <ul>
                            {patients.map((patient) => (
                                <li 
                                    key={patient.id}
                                    className="py-3 sm:py-4 p-2 mb-4 rounded-lg bg-[#F7F7F8] hover:bg-[#DAFEEF] hover:border hover:border-[#0FA968] cursor-pointer" 
                                    onClick={() => !loading && handleProfileSelect(patient.id)}
                                >
                                    <div className="flex items-center">
                                        <div className="flex-shrink-0">
                                            <div className="ms-2 w-14 h-14 rounded-xl bg-[#0FA968] flex items-center justify-center">
                                                <User className="w-8 h-8 text-white" />
                                            </div>
                                        </div>
                                        <div className="min-w-0 ms-4">
                                            <div className='flex items-center'>
                                                <p className="font-robot text-[20px] text-[#27272A] truncate" style={{ fontWeight: "700" }}>
                                                    {getFullName(patient)}
                                                </p>
                                                <span className='font-robot flex items-center ms-2 p-0.5 bg-white text-[14px] text-[#27272A] rounded-xl'>
                                                    <img src={getGenderImage(patient.genderCode)} alt='Gender' className='mr-1 h-4 w-4' />
                                                    {getGenderText(patient.genderCode)}
                                                </span>
                                            </div>

                                            <p className="font-robot text-[14px] text-[#27272A] truncate" style={{ fontWeight: "400" }}>
                                                {calculateAge(patient.dob)} ({formatDate(patient.dob)})
                                            </p>
                                            <p className="font-robot text-[12px] text-gray-500 truncate">
                                                MRN: {patient.mrno}
                                            </p>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}

                </div>
            </div>
        </div>
    </div>
    </>
  )
}

export default SelectPatientProfile